import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Heart } from 'lucide-react';
import { useFavorites } from '../context/FavoritesContext';
import { HeaderLogo } from './Logo';
import AudioPlayer from './AudioPlayer';

const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { favorites } = useFavorites();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'Explore Spas', path: '/spas' },
        { name: 'Blog', path: '/blog' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'About', path: '/about' },
        { name: 'Contact', path: '/contact' },
    ];

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const handleLogoClick = () => {
        if (location.pathname === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate('/');
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        }
    };

    return (
        <header 
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? 'glass-header shadow-sm' : 'bg-transparent'
            }`}
            data-testid="header"
        >
            <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <button 
                        onClick={handleLogoClick}
                        className="flex items-center gap-3 cursor-pointer" 
                        data-testid="header-logo"
                    >
                        <HeaderLogo />
                    </button>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`font-body text-sm tracking-wide transition-colors duration-200 ${
                                    isActive(link.path)
                                        ? 'text-[#C8A86B]'
                                        : 'text-[#4A4E4D] hover:text-[#C8A86B]'
                                }`}
                                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                            >
                                {link.name}
                            </Link>
                        ))}
                    </nav>

                    {/* Right side actions */}
                    <div className="flex items-center gap-4">
                        {/* Favorites */}
                        <Link
                            to="/spas?favorites=true"
                            className="relative p-2 text-[#4A4E4D] hover:text-[#C8A86B] transition-colors"
                            data-testid="favorites-link"
                        >
                            <Heart className="w-5 h-5" />
                            {favorites.length > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C8A86B] text-white text-xs rounded-full flex items-center justify-center">
                                    {favorites.length}
                                </span>
                            )}
                        </Link>

                        {/* Audio Player */}
                        <AudioPlayer />

                        {/* Mobile Menu Button */}
                        <button
                            className="md:hidden p-2 text-[#4A4E4D]"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isMobileMenuOpen && (
                    <div className="md:hidden glass-effect rounded-2xl mt-2 p-4 animate-fade-in" data-testid="mobile-menu">
                        <nav className="flex flex-col gap-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`px-4 py-3 rounded-xl font-body text-sm transition-colors ${
                                        isActive(link.path)
                                            ? 'bg-[#C8A86B] text-white'
                                            : 'text-[#4A4E4D] hover:bg-[#D4DED5]/30'
                                    }`}
                                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
};

export default Header;
